import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { BeeChatViewModel, IBeeChatTrackingFrom, HELPER_BEE_OPENED_STORAGE_KEY } from './BeeChatViewModel';
import { useSubscription } from '@meraki-internal/state';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { CommentQuestionIcon } from '../theme/Icons';
import { useCanChatWithBea } from './useCanChatWithBea';

const useStyles = createUseStyles({
    '@keyframes beaSonar': {
        '0%': {
            transform: 'translate(-50%, -50%) scale(1)',
            borderColor: 'rgba(255, 255, 255, 0.7)'
        },
        '100%': {
            transform: 'translate(-50%, -50%) scale(1.45)',
            borderColor: 'transparent'
        }
    },
    sonarContainer: {
        position: 'relative',
        width: 38,
        height: 38,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    sonarRing: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: '2px solid transparent',
        animation: '$beaSonar 2.5s ease-out infinite',
        pointerEvents: 'none',
        zIndex: 100,
        willChange: 'transform'
    },
    beaButton: {
        width: 38,
        height: 38,
        padding: 0,
        cursor: 'pointer',
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    beaButtonWithCircle: {
        borderRadius: '50%',
        border: '2px solid white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        background: 'var(--ion-color-primary)'
    },
    beaButtonWithoutCircle: {
        background: 'transparent',
        border: 'none',
        outline: 'none',
        boxShadow: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        appearance: 'none'
    },
    minimizedBadge: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: 'var(--ion-color-danger)',
        border: '2px solid white',
        zIndex: 101
    }
});

export interface IBeaSonarButtonProps {
    trackingFrom: IBeeChatTrackingFrom;
    'data-id'?: string;
}

export const BeaSonarButton: FC<IBeaSonarButtonProps> = ({
    trackingFrom,
    'data-id': dataId
}) => {
    const classes = useStyles();
    const storageProvider = useInstance(AsyncStorageProvider);
    const chatVM = useInstance(BeeChatViewModel);
    useSubscription(() => chatVM);

    const [hasOpenedChat, setHasOpenedChat] = useState<boolean>(true); // Default to true to avoid flash

    useAsyncEffect(async () => {
        const hasOpened = await storageProvider.getBooleanProvider(HELPER_BEE_OPENED_STORAGE_KEY).get();
        setHasOpenedChat(Boolean(hasOpened));
    }, [chatVM.state]);

    // Trigger pings when component mounts and user hasn't opened chat
    useEffect(() => {
        if (!hasOpenedChat) {
            chatVM.triggerPings('header');
        }
    }, [hasOpenedChat, chatVM]);

    const canChat = useCanChatWithBea();
    if (!canChat) {
        return null;
    }

    const onClickButton = () => {
        chatVM.open({ trackingFrom });
    };

    const isMinimized = chatVM.isMinimized();
    const showPings = chatVM.showHeaderPings();

    const shouldShowPings = !hasOpenedChat && showPings;
    const shouldShowCircle = hasOpenedChat || !showPings;

    return (
        <div data-id={dataId} className={classes.sonarContainer}>
            {shouldShowPings && (
                <>
                    <div className={classes.sonarRing} />
                    <div className={classes.sonarRing} style={{ animationDelay: '2.5s' }} />
                    <div className={classes.sonarRing} style={{ animationDelay: '5s' }} />
                </>
            )}
            {isMinimized && (
                <div className={classes.minimizedBadge} data-id="minimized-badge" />
            )}
            <button
                className={`${classes.beaButton} ${shouldShowCircle ? classes.beaButtonWithCircle : classes.beaButtonWithoutCircle}`}
                onClick={onClickButton}
                aria-label="Chat with Bea"
            >
                <CommentQuestionIcon color="light" customSize={21} />
            </button>
        </div>
    );
};
